import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, PackageOpen, Pencil } from "lucide-react";
import { Input, Select } from "./Warehouse";

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ sku: "", name: "", unit: "botol", kind: "produk_jadi", price: "" });
  const [editingId, setEditingId] = useState(null);
  const [confirmDelId, setConfirmDelId] = useState(null);
  const [q, setQ] = useState("");
  const emptyForm = { sku: "", name: "", unit: "botol", kind: "produk_jadi", price: "" };

  const load = async () => { setConfirmDelId(null); setRows((await api.get("/products")).data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: form.price ? parseFloat(form.price) : 0 };
    try {
      if (editingId) {
        await api.patch(`/products/${editingId}`, payload);
        toast.success("Produk diperbarui");
      } else {
        await api.post("/products", payload);
        toast.success("Produk dibuat");
      }
      setForm(emptyForm); setEditingId(null); setShow(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ sku: p.sku, name: p.name, unit: p.unit, kind: p.kind, price: p.price ? String(p.price) : "" });
    setShow(true);
  };
  const del = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Produk dihapus");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghapus produk"); }
  };

  const filtered = rows.filter(p => `${p.name} ${p.sku}`.toLowerCase().includes(q.toLowerCase()));
  const normName = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const nameCounts = {};
  rows.forEach(p => { const k = normName(p.name); if (k) nameCounts[k] = (nameCounts[k] || 0) + 1; });
  const dupeKeys = new Set(Object.keys(nameCounts).filter(k => nameCounts[k] > 1));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Master Data</p>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Produk</h1>
        </div>
        <button data-testid="new-product-btn" onClick={() => { setEditingId(null); setForm(emptyForm); setShow(!show); }} className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-2 active:scale-[0.98] transition-transform">
          <Plus size={16} /> Baru
        </button>
      </div>

      {dupeKeys.size > 0 && (
        <div data-testid="dupe-warning" className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-sm text-amber-800">
          <b>{dupeKeys.size} nama produk kembar terdeteksi.</b> Produk dengan nama persis sama bisa membuat analisa resi salah memilih produk (omset jadi tidak sesuai). Hapus atau ubah nama salah satu duplikat — kartu duplikat diberi bingkai kuning.
        </div>
      )}

      {show && (
        <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <Input testid="p-sku" label="SKU" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} required />
          <Input testid="p-name" label="Nama Produk" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <Input testid="p-price" label="Harga Jual (Rp) — dipakai untuk hitung omset" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Select testid="p-kind" label="Jenis" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} required options={[
              { value: "bahan_baku", label: "Bahan Baku" },
              { value: "produk_jadi", label: "Produk Jadi (Repack)" },
            ]} />
            <Select testid="p-unit" label="Satuan" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} required options={[
              { value: "botol", label: "Botol" },
              { value: "pcs", label: "Pcs" },
              { value: "kg", label: "Kg" },
              { value: "liter", label: "Liter" },
              { value: "pack", label: "Pack" },
            ]} />
          </div>
          <button data-testid="p-submit" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">{editingId ? "Simpan Perubahan" : "Simpan"}</button>
        </form>
      )}

      <div>
        <input data-testid="product-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama produk / SKU..." className="w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
      </div>

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-slate-500">Belum ada produk. Tambahkan bahan baku & produk jadi.</p>}
        {rows.length > 0 && filtered.length === 0 && <p className="text-sm text-slate-500">Tidak ada produk cocok dengan "{q}".</p>}
        {filtered.map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`} className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${dupeKeys.has(normName(p.name)) ? 'bg-amber-50/50 border-amber-400' : 'bg-white border-slate-200'}`}>
            <div className="flex gap-3 min-w-0 flex-1">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${p.kind === 'bahan_baku' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}><PackageOpen size={20} /></div>
              <div className="min-w-0">
                <p className="font-bold text-slate-900" style={{fontFamily:'Manrope'}}>{p.name}</p>
                <p className="text-xs text-slate-500 font-mono">{p.sku} · {p.unit} · {p.kind === 'bahan_baku' ? 'Bahan Baku' : 'Produk Jadi'}</p>
                <p className="text-xs font-bold text-slate-700 mt-0.5">Harga: Rp {Number(p.price || 0).toLocaleString('id-ID')}</p>
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0 items-center">
              <button data-testid={`edit-p-${p.id}`} onClick={() => startEdit(p)} className="w-10 h-10 rounded-lg text-blue-600 hover:bg-blue-50 flex items-center justify-center"><Pencil size={16} /></button>
              {confirmDelId === p.id ? (
                <div className="flex gap-2 w-full sm:w-auto justify-end">
                  <button data-testid={`confirm-del-p-${p.id}`} onClick={() => { setConfirmDelId(null); del(p.id); }} className="h-10 px-3 rounded-lg bg-red-600 text-white text-xs font-bold active:scale-95 transition-transform">Ya, Hapus</button>
                  <button data-testid={`cancel-del-p-${p.id}`} onClick={() => setConfirmDelId(null)} className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 text-xs font-bold">Batal</button>
                </div>
              ) : (
                <button data-testid={`del-p-${p.id}`} onClick={() => setConfirmDelId(p.id)} className="w-10 h-10 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center"><Trash2 size={16} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
